'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import {
    Align,
    Button,
    ButtonList,
    ButtonVariant,
    Divider,
    FlexBox,
    FlexItem,
    Icon,
    IconName,
    Justify,
    Modal,
    ModalBody,
    ModalFooter,
    Popover,
    PopoverDirection,
    PriceLevel,
    Title,
    TitleLevels,
    TitleMarkup,
    View,
} from '@bytel/trilogy-react';

import { getEsimSupportedDevices } from '@services/cms/esim-supported-devices';
import { httpService } from '@services/http';
import { logger } from '@services/loggers';
import { fetchProductByGencode } from '@services/products.service';

import { ConfigType } from '@app-types/api';
import { PRICE_TYPE } from '@app-types/price';
import { CartType, ProductTypeId, SimType } from '@app-types/product';
import { TagEventType, TagParamDataLayer } from '@app-types/tracking';

import { BoxInfo } from '@components/box-info';
import { Cms } from '@components/cms';
import { ListItemWithPrice } from '@components/list-item-with-price';
import { Loader } from '@components/loader';
import ErrorModal from '@components/modal/error-modal';

import useAbTest from '@hooks/use-abtest';
import { useApp } from '@hooks/use-app';
import { useCart } from '@hooks/use-cart';
import useTracking from '@hooks/use-tracking';

import { INFLATION_ABC_TEST, VariationEnum } from '@helpers/abtest-utils';
import { localApiPaths } from '@helpers/path';
import { homeRoute, optionRoute, rioRoute } from '@helpers/routes';
import { mapOptionsForTracking, mapProductOrOption } from '@helpers/tracking';

import { tagPanier } from '@constants/tags';

import { NavButtons } from '@tunnel/components/common';

import CartDeffered from './cart-deffered';
import CartMonthly from './cart-monthly';
import { EsimDescriptionModal } from './esim-description-modal';
import { SimsConf } from './sims-conf';

export function Cart({ config }: Readonly<{ config?: ConfigType }>) {
    const [compatibleSims, setCompatibleSims] = useState<SimType[]>([]);
    const [htmlEsimSupportedDevices, setHtmlEsimSupportedDevices] = useState<string>('');
    const [shouldShowEsimModal, setShouldShowEsimModal] = useState(false);
    const [shouldShowSupportedDevicesModal, setShouldShowSupportedDevicesModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldShowErrorModal, setShouldShowErrorModal] = useState(false);
    const hasTrackedRef = useRef(false);

    const { abTestVariant } = useAbTest(
        INFLATION_ABC_TEST,
        config?.ab_test_panier_taux_ouverture,
        'alerte_transparence_prix',
    );

    const { byouCart, updateCart, updateCartOptions } = useCart();
    const { goTo, hasOptionsAvailable } = useApp();
    const { sendTag, setCurrentPageTags } = useTracking();

    async function loadContextualizedSim(byouCart: CartType) {
        try {
            const sims = await httpService
                .post<SimType[]>(
                    localApiPaths.sims,
                    { gencode: byouCart.gencode },
                    {
                        headers: {
                            'x-process': 'GetBYOUSim',
                        },
                    },
                )
                .catch((error) => {
                    throw new Error(`HTTP error! status: ${error.status}`);
                });
            setCompatibleSims(sims);
            if (byouCart.product && !byouCart.sim) {
                const selectedSim = sims.find((sim) => sim.preferred);
                if (selectedSim) {
                    updateCart({ sim: selectedSim });
                }
            }
        } catch (error) {
            logger.error('Error fetching contextualized sims:', error);
            setShouldShowErrorModal(true);
        } finally {
            setIsLoading(false);
        }
    }

    async function getNeedElements(byouCart: CartType): Promise<void> {
        const neededElements = byouCart.product?.neededElements?.flatMap((el) =>
            el.alternatives.map((alt) => alt.identifier),
        );
        if (!neededElements) {
            return;
        }

        try {
            const products = await fetchProductByGencode(neededElements);
            if (!products || !byouCart) {
                return;
            }

            const existingOptionGencodes = new Set((byouCart.options ?? []).map((option) => option.gencode));

            const mobileFeeProduct = products.find((product) => product.typeId === ProductTypeId.FAI_FMD);
            updateCart({ mobileFees: mobileFeeProduct });

            products
                .filter(
                    (product) =>
                        product.typeId === ProductTypeId.OPTION && !existingOptionGencodes.has(product.gencode),
                )
                .forEach((product) => {
                    updateCartOptions(product);
                });
        } catch (error) {
            setShouldShowErrorModal(true);
            logger.error('Error fetching needed elements', error);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (!byouCart || Object.keys(byouCart).length === 0) {
                goTo(homeRoute.path);
                return;
            }

            await Promise.all([loadContextualizedSim(byouCart), getNeedElements(byouCart)]);
            try {
                const esimDevicesHtml = await getEsimSupportedDevices();
                setHtmlEsimSupportedDevices(esimDevicesHtml);
            } catch (error) {
                logger.error('ERROR GET getEsimSupportedDevices');
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (byouCart && !isLoading) {
            const tagCoParams: TagParamDataLayer = {
                ...tagPanier,
                panier: {
                    idPanier: byouCart?.tempId ?? '',
                    total: {
                        base: byouCart.sim?.price ?? 0,
                        recurring: byouCart.total,
                    },
                    produits: [],
                },
            };

            if (byouCart.product) {
                const mappedProduct = mapProductOrOption(byouCart.product);
                tagCoParams.panier?.produits.push(mappedProduct);
            }
            if (byouCart.sim) {
                const mappedProduct = mapProductOrOption({
                    ...byouCart.sim,
                    name: byouCart.sim.esim ? 'qr_esim' : 'Carte SIM NG',
                });
                tagCoParams.panier?.produits.push(mappedProduct);
            }
            if ((byouCart.options?.length || 0) > 0) {
                const mappedOptions = mapOptionsForTracking(byouCart.options);
                tagCoParams.panier?.produits.push(...mappedOptions);
            }
            if (!hasTrackedRef.current) {
                hasTrackedRef.current = true;
                setCurrentPageTags(tagCoParams);
                sendTag(tagCoParams, TagEventType.DATA);
            }
        }
    }, [
        byouCart?.tempId,
        byouCart?.total,
        byouCart?.sim?.price,
        byouCart?.sim?.esim,
        byouCart?.product?.gencode,
        byouCart?.options?.length,
        isLoading,
    ]);

    const backUrl = useMemo(() => {
        let hasOptions = hasOptionsAvailable;

        if (hasOptions === null && typeof window !== 'undefined') {
            const stored = localStorage.getItem('hasOptionsAvailable');
            if (stored !== null) {
                hasOptions = stored === 'true';
            }
        }

        if (hasOptions !== false) {
            return optionRoute.path;
        }
        return homeRoute.path;
    }, [hasOptionsAvailable]);

    if (!byouCart || Object.keys(byouCart).length === 0) {
        return null;
    }

    const { product, sim, total } = byouCart;
    if (!product) return null;

    const getTitleByPriceType = () => {
        if (sim?.priceType === PRICE_TYPE.DEFERRED) {
            return `À régler sur 1ère facture`;
        }

        return "À régler aujourd'hui";
    };

    const showDefferedColumn = () => {
        return (
            byouCart.sim?.priceType !== PRICE_TYPE.DEFERRED &&
            byouCart.mobileFees?.prices.type === PRICE_TYPE.DEFERRED &&
            byouCart.mobileFees?.price &&
            byouCart.mobileFees?.price > 0
        );
    };

    const getHeaderAmount = () => {
        if (!byouCart || !byouCart.sim) {
            return 0;
        }

        let amount = byouCart.sim.price || 0;
        const priceType = byouCart.sim.priceType;

        if (priceType === PRICE_TYPE.TODAY || priceType === PRICE_TYPE.DEFERRED) {
            if (byouCart.mobileFees?.prices.type === priceType) {
                amount += byouCart.mobileFees.price || 0;
            }

            if (byouCart.deliveryMode?.priceType === priceType) {
                amount += byouCart.deliveryMode.price || 0;
            }
        }

        return amount;
    };

    return (
        <>
            <div className='flex-box is-direction-column' style={{ flexGrow: '1' }}>
                <FlexBox justify={Justify.CENTER}>
                    <FlexItem
                        size={{
                            desktop: showDefferedColumn() ? 4 : 6,
                            mobile: 12,
                        }}
                    >
                        <CartMonthly total={total} showInflationAlert={abTestVariant === VariationEnum.VARIATION_A} />
                    </FlexItem>
                    <div
                        className={`is-flex  is-12-mobile ${showDefferedColumn() ? 'is-4-desktop' : 'is-6-desktop'} is-sticky-top-desktop`}
                    >
                        <BoxInfo header={CartSimHeader(getTitleByPriceType(), getHeaderAmount())}>
                            {isLoading ? (
                                <View flexable justify={Justify.CENTER} className='is-fullheight' align={Align.CENTER}>
                                    <Loader />
                                </View>
                            ) : (
                                <>
                                    <SimsConf
                                        product={product}
                                        sim={sim}
                                        compatibleSims={compatibleSims}
                                        config={config}
                                        onShowEsimModal={() => setShouldShowEsimModal(true)}
                                        onShowEsimCompatibleDevicesModal={() =>
                                            setShouldShowSupportedDevicesModal(true)
                                        }
                                    />
                                    {((byouCart.sim?.priceType === PRICE_TYPE.DEFERRED && byouCart.mobileFees) ||
                                        (byouCart.mobileFees &&
                                            byouCart.mobileFees?.prices.type !== PRICE_TYPE.DEFERRED)) && (
                                        <>
                                            <Divider iconName={IconName.PLUS} className='has-background-main-fade' />
                                            <Title level={TitleLevels.FIVE} markup={TitleMarkup.H3}>
                                                {byouCart.mobileFees?.name}
                                                <Popover
                                                    trigger={<Icon name={IconName.INFOS_CIRCLE} />}
                                                    direction={PopoverDirection.BOTTOM}
                                                >
                                                    Ces frais couvrent la mise en service et la configuration de votre
                                                    nouvelle ligne.
                                                </Popover>
                                            </Title>
                                            <ListItemWithPrice
                                                subTitle='À payer sur la 1ère facture'
                                                amount={byouCart?.mobileFees?.price || 0}
                                                subTitleClassName='has-text-weight-normal'
                                                titleClassName='has-text-white'
                                                id='SummaryDefferedBoxCartProducts'
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </BoxInfo>
                    </div>
                    {!isLoading && showDefferedColumn() && (
                        <div className='is-flex is-4-desktop is-12-mobile is-fullwidth is-sticky-top-desktop'>
                            <CartDeffered isFromCart />
                        </div>
                    )}
                </FlexBox>
            </div>
            <NavButtons
                isNextDisabled={!byouCart.sim}
                nextLabel='Étape suivante'
                backUrl={backUrl}
                nextUrl={rioRoute.path}
            />
            <ErrorModal
                active={shouldShowErrorModal}
                title='Une erreur est survenue'
                description='Veuillez réessayer ultérieurement'
                primaryCta={{
                    label: 'Fermer',
                    action: () => {
                        setShouldShowErrorModal(false);
                        goTo(homeRoute.path);
                    },
                }}
                onClose={() => {
                    setShouldShowErrorModal(false);
                    goTo(homeRoute.path);
                }}
            />

            <Modal
                title='Comment installer votre eSIM ?'
                onClose={() => setShouldShowEsimModal(false)}
                active={shouldShowEsimModal}
                className='has-text-left'
            >
                <ModalBody>
                    <EsimDescriptionModal />
                </ModalBody>
                <ModalFooter>
                    <ButtonList>
                        <Button onClick={() => setShouldShowEsimModal(false)} variant={ButtonVariant.PRIMARY}>
                            Fermer
                        </Button>
                    </ButtonList>
                </ModalFooter>
            </Modal>
            <Modal
                className='has-text-left'
                title='Liste des téléphones compatibles eSIM'
                active={shouldShowSupportedDevicesModal}
                onClose={() => setShouldShowSupportedDevicesModal(false)}
            >
                <ModalBody>
                    <Cms className='has-text-left' content={htmlEsimSupportedDevices} />
                </ModalBody>
                <ModalFooter>
                    <ButtonList>
                        <Button
                            onClick={() => setShouldShowSupportedDevicesModal(false)}
                            variant={ButtonVariant.PRIMARY}
                        >
                            Fermer
                        </Button>
                    </ButtonList>
                </ModalFooter>
            </Modal>
        </>
    );
}

function CartSimHeader(title: string, amount: number) {
    return (
        <ListItemWithPrice
            mainTitle={title}
            amount={amount}
            subTitleClassName='has-text-weight-normal'
            titleClassName='has-text-white'
            className='has-text-white'
            priceLevel={PriceLevel.FIVE}
        />
    );
}
